import { useEffect, useRef } from "react";

// Each row has its own marketing-relevant content — reflects BadanIbex's actual services
const ROWS: { text: string; speed: number; opacity: number; depth: number }[] = [
  {
    text: "launch.product() · scale.revenue() · deploy.saas() · build.empire() · ship.fast() · grow.users() · automate.workflow() · dominate.market() · ",
    speed: 28, opacity: 0.9, depth: 1,
  },
  {
    text: "digital.transform(business) · integrate.api() · analyze.data() · engage.customer() · optimize.funnel() · monetize.traffic() · ",
    speed: 38, opacity: 0.55, depth: 2,
  },
  {
    text: "[ منتجات رقمية ] · [ حلول SaaS ] · [ أتمتة الأعمال ] · [ تحويل رقمي ] · [ نمو مستدام ] · [ تجارة إلكترونية ] · ",
    speed: 22, opacity: 0.7, depth: 1,
  },
  {
    text: "secure(payment.gateway) · protect(user.data) · encrypt(api.keys) · validate(session.token) · shield(platform.core) · ",
    speed: 44, opacity: 0.4, depth: 2,
  },
  {
    text: "products: 50+ · customers: 2.4K+ · rating: 4.8★ · uptime: 99.9% · support: 24/7 · delivery: instant · ",
    speed: 32, opacity: 0.85, depth: 1,
  },
  {
    text: "build(landing.page) · create(saas.app) · design(ui.system) · publish(digital.product) · configure(automation) · export(revenue.report) · ",
    speed: 50, opacity: 0.35, depth: 2,
  },
  {
    text: "[ أطلق منتجك ] · [ وسّع نطاقك ] · [ حقق دخلاً رقمياً ] · [ اكتسب عملاء ] · [ بنِ علامتك ] · [ افتح فرصة جديدة ] · ",
    speed: 26, opacity: 0.65, depth: 1,
  },
  {
    text: "api.connect(stripe) · api.connect(sendgrid) · api.connect(cloudflare) · api.connect(vercel) · api.connect(supabase) · api.connect(openai) · ",
    speed: 42, opacity: 0.45, depth: 2,
  },
  {
    text: "BadanIbex.launch(53+) · BadanIbex.scale(83 GB) · BadanIbex.automate( 9 Projects) · BadanIbex.monetize($100K) · BadanIbex.grow(12%) · BadanIbex.dominate(3%) · ",
    speed: 20, opacity: 1.0, depth: 1,
  },
  {
    text: "render(hero.section) · animate(ui.transition) · optimize(core.vitals) · compress(assets) · cache(static.content) · prefetch(next.route) · ",
    speed: 36, opacity: 0.5, depth: 2,
  },
];

function NeuralInteractiveLayer() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let rafPending = false;
    let lastX = 0;
    let lastY = 0;

    const move = (e: MouseEvent) => {
      lastX = e.clientX;
      lastY = e.clientY;
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(() => {
        if (!containerRef.current) { rafPending = false; return; }
        containerRef.current.style.setProperty("--x", `${lastX}px`);
        containerRef.current.style.setProperty("--y", `${lastY}px`);
        const dx = ((lastX - window.innerWidth / 2) / (window.innerWidth / 2)) * 10;
        const dy = ((lastY - window.innerHeight / 2) / (window.innerHeight / 2)) * 10;
        containerRef.current.style.setProperty("--mouse-dx", String(dx));
        containerRef.current.style.setProperty("--mouse-dy", String(dy));
        rafPending = false;
      });
    };

    window.addEventListener("mousemove", move, { passive: true });
    return () => window.removeEventListener("mousemove", move);
  }, []);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none overflow-hidden z-[1] select-none neural-container"
      style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "13px" }}
    >
      {/* Mouse-follow gold glow */}
      <div
        className="absolute w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none"
        style={{
          left: "var(--x, 50%)",
          top: "var(--y, 50%)",
          transform: "translate(-50%, -50%)",
          backgroundColor: "color-mix(in srgb, var(--gold) 7%, transparent)",
        }}
      />

      {/* Depth layer 2 — slower parallax, dimmer (background plane) */}
      <div
        className="absolute inset-0 transition-transform duration-300 ease-out"
        style={{
          transform: "translate3d(calc(var(--mouse-dx, 0) * -2px), calc(var(--mouse-dy, 0) * -2px), 0)",
        }}
      >
        {ROWS.filter(r => r.depth === 2).map((row, i) => (
          <div
            key={`bg-${i}`}
            className="whitespace-nowrap leading-[2.2] tracking-widest uppercase neural-line"
            style={{
              color: "var(--gold)",
              opacity: row.opacity * 0.18,
              animationDuration: `${row.speed * 1.6}s`,
              animationDelay: `${i * 3.5}s`,
            }}
          >
            {row.text.repeat(5)}
          </div>
        ))}
      </div>

      {/* Depth layer 1 — faster parallax, brighter (foreground plane) */}
      <div
        className="absolute inset-0 transition-transform duration-200 ease-out"
        style={{
          transform: "translate3d(calc(var(--mouse-dx, 0) * -1px), calc(var(--mouse-dy, 0) * -1px), 0)",
        }}
      >
        {ROWS.filter(r => r.depth === 1).map((row, i) => (
          <div
            key={`fg-${i}`}
            className="whitespace-nowrap leading-[2.6] tracking-wider neural-line"
            style={{
              color: i === 4 ? "var(--cyan)" : "var(--gold)",
              opacity: row.opacity * 0.22,
              animationDuration: `${row.speed}s`,
              animationDelay: `${i * 2.1}s`,
            }}
          >
            {row.text.repeat(5)}
          </div>
        ))}
      </div>
    </div>
  );
}

export default NeuralInteractiveLayer;